import React, { useState, useCallback } from 'react';
import { Contract } from 'ethers';
import { useSend } from '@sc1/common/hooks';
import { schedule, ScionTimelock } from '@sc1/common';
import { Row, Card, Heading, Input, Text, Box } from '../core';
import { StatusButton } from '../StatusButton';
import { ScheduleIcon } from '../icons';
import type { CardProps } from 'theme-ui';

interface BooleanAddressMethodProps<T, F> extends CardProps {
  contract: T | undefined;
  timelock?: ScionTimelock;
  method: F;
  label: string;
  labelTrue: string;
  labelFalse: string;
  disabled: boolean;
  disableTrue?: boolean;
  disableFalse?: boolean;
  hint?: string;
}
export const BooleanAddressMethod = <
  T extends Contract,
  F extends keyof T & string
>({
  contract,
  timelock,
  method,
  label,
  labelTrue,
  labelFalse,
  disabled,
  disableTrue,
  disableFalse,
  hint,
  ...rest
}: BooleanAddressMethodProps<T, F>): JSX.Element => {
  const [address, setAddress] = useState<string>('');

  const { sendTx, txStatus } = useSend();
  const { sendTx: sendTx1, txStatus: txStatus1 } = useSend();

  // TODO: refactor true/false into one fn with bool prop
  const handleSubmitTrue = useCallback(() => {
    if (!contract) return;
    if (address === '') return; // avoid empty
    const params = [address, true] as Parameters<T[F]>;
    // TODO: is there a way to typecheck that Parameters<T[F]> here is [string, boolean] ?
    if (timelock) {
      return schedule(sendTx, timelock, contract, method, params);
    }
    sendTx(contract, method, params);
  }, [contract, timelock, address, method, schedule, sendTx]);

  const handleSubmitFalse = useCallback(() => {
    if (!contract) return;
    if (address === '') return; // avoid empty
    const params = [address, false] as Parameters<T[F]>;
    // TODO: is there a way to typecheck that Parameters<T[F]> here is [string, boolean] ?
    if (timelock) {
      return schedule(sendTx1, timelock, contract, method, params);
    }
    sendTx1(contract, method, params);
  }, [contract, timelock, address, method, schedule, sendTx1]);

  return (
    <Card {...rest}>
      {hint && (
        <Text variant="hint" mb={1}>
          {hint}
        </Text>
      )}
      <Heading as="h3" mb={2}>
        {label}
      </Heading>
      <Row variant="collapse">
        <Input
          disabled={disabled}
          value={address}
          placeholder="0x..."
          onChange={(e) => setAddress(e.target.value)}
        />
        <Box my={1} mx={1} />
        <StatusButton
          disabled={disabled || disableTrue}
          onClick={handleSubmitTrue}
          status={txStatus}
          suffix={timelock ? <ScheduleIcon /> : null}
        >
          {labelTrue}
        </StatusButton>
        <Box my={1} mx={1} />
        <StatusButton
          disabled={disabled || disableFalse}
          onClick={handleSubmitFalse}
          status={txStatus1}
          suffix={timelock ? <ScheduleIcon /> : null}
        >
          {labelFalse}
        </StatusButton>
      </Row>
    </Card>
  );
};
